<template>
  <PortalWidget
    :widget="{
      title: $t('CAMPAIGN_STEP_ROUTING_SELECTED_HEADER')
    }"
  >
    <template v-slot:headerContent>
      <div class="header-extra">
        <input
          v-model="filters.name.value"
          type="search"
          class="form-control input-md"
          :placeholder="$t('SEARCH')"
        >
      </div>
    </template>
    <div
      v-if="isTargetPassthrough"
      v-t="'CAMPAIGN_WARNING_TARGET_GROUPS_PASSTHROUGH'"
      class="bs-callout bs-callout-warning description m-t-0"
    />

    <div class="grid_table_holder">
      <v-table
        :data="displayRoutes"
        :current-page.sync="currentPage"
        :page-size="10"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty table-condensed"
        cellpadding="0"
        cellspacing="0"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <!-- Empty header for actions -->
            <th v-if="isMobileView" />

            <v-th
              v-if="!context.isRPC"
              v-t="'CAMPAIGN_STEP_ROUTING_SELECTED_PRIORITY'"
              sort-key="priority.priority"
              default-sort="asc"
              class="column-sortable"
            />

            <v-th
              v-if="!context.isRPC"
              v-t="'CAMPAIGN_STEP_ROUTING_SELECTED_WEIGHT'"
              sort-key="priority.weight"
              class="column-sortable"
            />
            <v-th
              v-t="'CAMPAIGN_STEP_ROUTING_SELECTED_NAME'"
              :sort-key="item => item.callTarget.name || item.callTargetGroup.name || item.callPingTree.name"
              class="column-sortable width-20-percent"
            />
            <v-th
              v-t="'CAMPAIGN_STEP_ROUTING_SELECTED_NUMBER'"
              sort-key="number"
              class="column-sortable"
            />
            <v-th
              v-t="'CAMPAIGN_STEP_ROUTING_SELECTED_TYPE'"
              :sort-key="item => item.conversionSettings[0].conversionType"
              class="column-sortable"
            />
            <v-th
              v-t="'CAMPAIGN_STEP_ROUTING_SELECTED_REVENUE'"
              :sort-key="item => item.conversionSettings[0].conversionValue"
              class="column-sortable text-right"
            />
            <v-th v-if="context.mode === 'view'">
              &nbsp;
            </v-th>

            <v-th
              v-t="'STATUS_TEXT'"
              :sort-key="item => item.callTarget.enabled || item.callTargetGroup.enabled"
              class="text-center column-sortable"
            />

            <th
              v-if="!isMobileView"
              v-t="'ACTIONS_COLUMN_HEADER'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <template v-for="callRoute in displayData">
            <tr
              v-if="!(isMobileView && context.activeActionRouteId == callRoute.id)"
              :key="callRoute.id"
              class="vertical-top"
            >
              <td v-if="isMobileView">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  @click="context.activateRouteActions(callRoute.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>

              <!-- Priority -->
              <td v-if="!context.isRPC">
                <NumberStepperInput
                  v-model="callRoute.priority.priority"
                  min="1"
                  input-class="input-xs"
                  @input="context.updateCallRoute(callRoute)"
                />
              </td>

              <!-- Weight -->
              <td v-if="!context.isRPC">
                <NumberStepperInput
                  v-model="callRoute.priority.weight"
                  min="1"
                  input-class="input-xs"
                  @input="context.updateCallRoute(callRoute)"
                />
              </td>

              <!-- Display Name -->
              <td>
                <LinkedName
                  v-if="callRoute.callTarget"
                  :href="getHref('dashboard.target.edit', { name: callRoute.callTarget.name, targetId: callRoute.callTarget.id })"
                  :title="callRoute.callTarget.name"
                />

                <LinkedName
                  v-if="callRoute.callTargetGroup"
                  :href="getHref('dashboard.targetgroup.edit', { name: callRoute.callTargetGroup.name, targetGroupId: callRoute.callTargetGroup.id })"
                  :title="callRoute.callTargetGroup.name"
                />

                <LinkedName
                  v-if="callRoute.callPingTree"
                  :href="getHref('dashboard.ringtree.edit', { name: callRoute.callPingTree.name, pingTreeId: callRoute.callPingTree.id })"
                  :title="callRoute.callPingTree.name"
                />
              </td>

              <!-- Destination -->
              <td
                v-tooltip.right.hover="getNumberDestinationRaw(callRoute)"
                class="max-width-80 overflow-ellipsis"
                aria-hidden="true"
              >
                {{ getNumberDestination(callRoute) }}
              </td>

              <!-- Conversion -->
              <td>
                <div
                  class="max-width-100 overflow-ellipsis"
                  :title="getConversionTypeName(callRoute)"
                >
                  {{ getConversionTypeName(callRoute) }}
                </div>
                <div
                  v-if="getIsConversionTypeCallLength(callRoute)"
                  class="f-s-10"
                >
                  {{ displayConversionTypeDetails(callRoute) }}
                </div>
              </td>

              <!-- Revenue -->
              <td class="text-right">
                {{ getRevenue(callRoute) }}
              </td>

              <!-- TODO: Delete, not used -->
              <td v-if="context.routeContainer.id && (context.mode === 'view')">
                <button
                  class="btn btn-list-function width-20"
                  @click="context.viewCallRoute(callRoute.id)"
                >
                  <i
                    class="typcn typcn-edit"
                    aria-hidden="true"
                  />
                </button>
              </td>

              <td class="text-center">
                <TargetStatus :entity="callRoute.callTarget || callRoute.callTargetGroup || callRoute.callPingTree" />
              </td>

              <td v-if="!isMobileView">
                <RoutingTargetActions
                  :call-route="callRoute"
                  @activate-route-actions="context.activateRouteActions"
                  @edit-call-route="context.editCallRoute"
                  @delete-route="context.deleteRoute"
                />
              </td>
            </tr>

            <!-- Actions Mobile collapsed view - Start -->
            <tr
              v-if="isMobileView && context.activeActionRouteId == callRoute.id"
              :key="`${callRoute.id}_actions`"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <RoutingTargetActions
                  :call-route="callRoute"
                  @activate-route-actions="context.activateRouteActions"
                  @edit-call-route="context.editCallRoute"
                  @delete-route="context.deleteRoute"
                />
              </td>
            </tr>
            <!-- Actions Mobile collapsed view - End -->
          </template>
        </tbody>
      </v-table>
      <div class="pagination-min">
        <div class="text-center p-0">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="5"
          />
        </div>
      </div>
    </div>
    <div
      v-if="!displayRoutes.length"
      class="row"
    >
      <div
        v-t="'CAMPAIGN_STEP_ROUTING_SELECTED_DEFAULT'"
        class="col-xs-12"
      />
    </div>
  </PortalWidget>
</template>

<script>
import { defineComponent } from 'vue';
import sharedMessages from '@/core/locales';
import messages from '../locales';
import { isMobile } from '@/core/utilities';
import PortalWidget from '@/core/components/PortalWidget.vue';
import LinkedName from '@/core/components/LinkedName/LinkedName.vue';
import TargetStatus from '@/core/components/Status/Status.vue';
import RoutingTargetActions from './RoutingTargetActions.vue';
import NumberStepperInput from '@/core/components/NumberStepper/NumberStepperInput.vue';
import { secondsToTextFormat } from '@/core/filters/secondsToTextFormat';
import {
  $ngStateService,
} from '@/angularBridgeCode/ringbaPortal.servicesModule';

export default defineComponent({
  name: 'RoutingPlanEdit',
  components: {
    PortalWidget,
    LinkedName,
    TargetStatus,
    NumberStepperInput,
    RoutingTargetActions,
  },
  inject: {
    context: {
      from: 'routingPlansContext',
    },
  },
  data() {
    return {
      isMobileView: isMobile,
      currentPage: 1,
      totalPages: 0,
      filters: {
        name: {
          value: '',
          keys: ['callTarget.name', 'callTargetGroup.name', 'callPingTree.name'],
        },
      },
    };
  },

  computed: {
    routeContainer() {
      return this.context.routeContainer;
    },
    displayRoutes() {
      return this.context.routeContainer?.routes || [];
    },
    isTargetPassthrough() {
      return this.context.getHasTargetGroups()
        && this.context.routeContainer.rtb.enabled
        && this.context.routeContainer.rtb.passThroughBid.enabled;
    },
    conversionCallLengthEventMap() {
      return {
        OnIncomingCall: this.$t('ROUTING_CONVERSION_CALL_INCOMING'),
        OnDial: this.$t('ROUTING_CONVERSION_CALL_DIAL'),
        OnConnectedCall: this.$t('ROUTING_CONVERSION_CALL_CONNECT'),
      };
    }
  },
  methods: {
    getHref(state, params) {
      return $ngStateService.href(
        state,
        params,
        { absolute: true }
      );
    },
    isPingTree(callRoute) {
      return callRoute?.callPingTree != null;
    },
    getNumberDestinationRaw(callRoute) {
      return callRoute.callTarget.instructions.callType === 'number'
        ? callRoute.callTarget.instructions.number
        : callRoute.callTarget.instructions.sip.number;
    },
    getNumberDestination(callRoute) {
      if (this.isPingTree(callRoute)) {
        return this.$t('CAMPAIGN_STEP_ROUTING_SELECTED_NUMBER_DYNAMIC');
      }

      return this.getNumberDestinationRaw(callRoute);
    },
    getConversionTypeName(callRoute) {
      if (!callRoute.conversionSettings) {
        return '';
      }

      const conversionType = callRoute.conversionSettings[0].conversionType;
      return this.context.getConversionTypeName(conversionType);
    },
    getIsConversionTypeCallLength(callRoute) {
      return callRoute.conversionSettings?.[0].conversionType === 'callLength';
    },
    displayConversionTypeDetails(callRoute) {
      const conversionSettings = callRoute.conversionSettings[0];
      const callLength = secondsToTextFormat(conversionSettings.conversionArgs.callLengthInSeconds) || '';
      const callEvent = this.conversionCallLengthEventMap[conversionSettings.conversionArgs.startTimerEvent];
      return `${callEvent} | ${callLength}`;
    },
    getRevenue(callRoute) {
      if (this.isPingTree(callRoute)) {
        return '';
      }

      return callRoute.conversionSettings
        ? '$' + callRoute.conversionSettings[0].conversionValue
        : '';
    }
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
});

</script>
